import { inject, Injectable } from '@angular/core';
import { AppPathsMap } from '@core/routing/app-paths';
import { ImpersonationService } from '@core/services/impersonation.service';
import { LoggerService } from '@kerberos-compliance/compliance-shared-ui';
import { HankoService } from '@/auth/services/hanko.service';
import { KerberosAuthService } from '@/auth/services/kerberos-auth.service';
import type { AuthStateTokens } from '@/auth/types/auth.types';
import { LogoutReason } from '@/auth/types/auth.types';
import { UserProfileFacade } from '@/user/facades/user-profile.facade';
import { BaseAuthService } from './base-auth.service';

/**
 * The platform tokens of the session being ended, captured before the caller clears storage.
 */
export type LogoutSessionTokens = Pick<AuthStateTokens, 'accessTokenKerberos' | 'refreshTokenKerberos'>;

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseAuthService {
  /**
   * Longest the sign-out waits for each best-effort backend call (last-visited-URL reset,
   * platform sign-out) before it continues. Keeps a logout triggered from an error page -
   * where an unreachable backend is often the reason the user is there - from stalling.
   */
  private static readonly LogoutRequestTimeoutMs = 3000;

  private readonly userProfileFacade = inject(UserProfileFacade);

  private readonly impersonationService = inject(ImpersonationService);

  private readonly hankoService = inject(HankoService);

  private readonly kerberosAuthService = inject(KerberosAuthService);

  private readonly logger = inject(LoggerService);

  /**
   * Logs the user out of the application: ends the Hanko session and returns to
   * the login page. An impersonated session instead returns to the admin desk.
   *
   * @param {LogoutReason} [logoutReason] The reason for the logout, which can affect how the logout process is handled.
   * If the reason is "Manual", it updates the user profile to clear the last visited URL.
   * @param {LogoutSessionTokens} [sessionTokens] The platform tokens of the session being ended.
   * @returns {Promise<void>} A promise that resolves when the logout process is completed.
   */
  public async logout(logoutReason?: LogoutReason, sessionTokens?: LogoutSessionTokens): Promise<void> {
    const isImpersonationActive = this.impersonationService.isActive();
    const isBypassExternalAuth = isImpersonationActive || this.impersonationService.shouldBypassExternalAuth();

    // Only update user profile on manual logout - skip when session expired (token is invalid)
    if (!isImpersonationActive && logoutReason === LogoutReason.Manual) {
      await this.clearLastVisitedUrl();
    }

    if (isBypassExternalAuth) {
      const adminReturnUrl = this.impersonationService.buildAdminReturnUrl(
        sessionTokens?.accessTokenKerberos ?? undefined,
      );
      this.impersonationService.clear();
      if (adminReturnUrl) {
        this.navigateToUrl(adminReturnUrl);
      } else {
        this.redirectToOrigin();
      }
      return;
    }

    // An expired session failed its refresh, so the backend would only reject the sign-out.
    if (logoutReason !== LogoutReason.SessionExpired && sessionTokens?.refreshTokenKerberos) {
      await this.signOutPlatformSession(sessionTokens.refreshTokenKerberos);
    }

    try {
      await this.hankoService.logout();
    } catch (error) {
      this.logger.warn('Hanko logout failed, continuing with local sign-out', 'AuthService', error);
    }

    // A full-page load rather than an in-app navigation: the caller has already
    // cleared the session storage, so this hands a genuinely anonymous visitor to
    // the app initializer and tears down any in-flight WebAuthn request.
    this.redirectToPath(AppPathsMap.Login);
  }

  /**
   * Clears the persisted last-visited URL so the next sign-in starts from the
   * dashboard rather than the page the user deliberately left.
   *
   * Best-effort by design: this is an authenticated write issued while the
   * session is being torn down, so the platform token may already be gone (an
   * expired refresh, a prior 401). A rejection here must never propagate —
   * it would abort the rest of `logout()`, leaving the user signed in with the
   * Hanko session still open and no redirect, and surface as an unhandled
   * rejection in Sentry (AML-DESK-AW).
   *
   * Time-bounded for the same reason: the request has no transport timeout, so an
   * unreachable backend would otherwise hold the sign-out — and the user — on the
   * page until the browser gives up. The write is left in flight; the redirect that
   * follows tears it down.
   *
   * @returns {Promise<void>} Resolves once the write has been attempted.
   */
  private async clearLastVisitedUrl(): Promise<void> {
    try {
      await this.withLogoutTimeout(this.userProfileFacade.updateUserPreferences({ lastVisitedUrl: undefined }));
    } catch (error) {
      this.logger.warn('Could not clear the last visited URL during logout', 'AuthService', error);
    }
  }

  /**
   * Ends the platform session on the backend so its device token and cookie are disposed.
   * Best-effort and time-bounded like `clearLastVisitedUrl()`: the local sign-out always continues.
   *
   * @param {string} refreshToken - The refresh token of the session being ended.
   * @returns {Promise<void>} Resolves once the sign-out has been attempted.
   */
  private async signOutPlatformSession(refreshToken: string): Promise<void> {
    try {
      await this.withLogoutTimeout(this.kerberosAuthService.signOut(refreshToken));
    } catch (error) {
      this.logger.warn('Platform sign-out failed, continuing with local sign-out', 'AuthService', error);
    }
  }

  /**
   * Waits for a logout request, but no longer than `LogoutRequestTimeoutMs`. The request is left in flight
   * on timeout; the redirect that follows tears it down.
   *
   * @param {Promise<unknown>} request - The request to wait for.
   * @returns {Promise<void>} Resolves when the request settles successfully or the timeout elapses; rejects with its error.
   */
  private async withLogoutTimeout(request: Promise<unknown>): Promise<void> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    try {
      await Promise.race([
        request,
        new Promise<void>((resolve) => {
          timeoutId = setTimeout(resolve, AuthService.LogoutRequestTimeoutMs);
        }),
      ]);
    } finally {
      clearTimeout(timeoutId);
    }
  }

  /**
   * Redirects the user to the origin of the current location if possible.
   * If the `location.assign` method is available, it uses it; otherwise, it directly modifies `location.href`.
   * If the origin is unavailable, redirects to '/'.
   *
   * @returns {void} This method does not return a value.
   */
  private redirectToOrigin(): void {
    const locationReference = globalThis.location;
    const target = locationReference?.origin ?? '/';
    if (locationReference && typeof locationReference.assign === 'function') {
      locationReference.assign(target);
    } else if (locationReference) {
      // eslint-disable-next-line unicorn/prefer-location-assign -- fallback for location stubs without assign()
      locationReference.href = target;
    }
  }

  /**
   * Redirects the browser to a path relative to the current origin.
   *
   * @param {string} path - The path to redirect to (e.g. the Hanko login route)
   */
  private redirectToPath(path: string): void {
    const locationReference = globalThis.location;
    const target = `${locationReference?.origin ?? ''}/${path}`;
    if (locationReference && typeof locationReference.assign === 'function') {
      locationReference.assign(target);
    } else if (locationReference) {
      // eslint-disable-next-line unicorn/prefer-location-assign -- fallback for location stubs without assign()
      locationReference.href = target;
    }
  }

  /**
   * Redirects the browser to the specified URL using the most appropriate navigation method available.
   * The URL is validated against the trusted AML Admin desk origin first to prevent
   * open-redirect; an untrusted or malformed URL falls back to a safe same-origin redirect.
   *
   * @param {string} url - The URL to navigate to.
   * @returns {void} This method does not return a value.
   */
  private navigateToUrl(url: string): void {
    if (!this.isTrustedAdminReturnUrl(url)) {
      this.logger.warn('Refusing to navigate to an untrusted admin return URL', 'AuthService', url);
      this.redirectToOrigin();
      return;
    }
    const locationReference = globalThis.location;
    if (locationReference && typeof locationReference.replace === 'function') {
      locationReference.replace(url);
    } else if (locationReference && typeof locationReference.assign === 'function') {
      locationReference.assign(url);
    } else {
      globalThis.open(url, '_self');
    }
  }

  /**
   * Whether the URL's origin matches the configured AML Admin desk base URL.
   * Guards the admin-return navigation sinks (location.replace/assign,
   * globalThis.open) against open-redirect: the app only ever navigates away to
   * the trusted admin origin (X-05). Comparing the full origin pins the scheme
   * to whatever the environment configures (https in prod/stage/dev, http only
   * on localhost), so an http downgrade in an https environment is rejected — as
   * are non-navigational schemes (javascript:, data:), whose origin never matches.
   *
   * @param {string} url - The candidate admin-return URL.
   * @returns {boolean} True when the URL is safe to navigate to.
   */
  private isTrustedAdminReturnUrl(url: string): boolean {
    const allowedBaseUrl = this.environmentService.adminDeskBaseUrl;
    if (!url || !allowedBaseUrl) {
      return false;
    }
    try {
      return new URL(url).origin === new URL(allowedBaseUrl).origin;
    } catch {
      return false;
    }
  }
}
